import { type FC, type ReactNode, memo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import InfoIcon from 'assets/svg/app/info.svg'

import { type WeiSource, wei } from '@kwenta/wei'
import Currency from './Currency'
import Spacer from './Spacer'
import { Body } from './Text'
import { FlexDivCol, FlexDivRow } from './layout/flex'

type PnlTooltipProps = {
	pnl: WeiSource
	feesPaid: WeiSource
	funding: WeiSource
	interestCharged?: WeiSource
	totalPnl: WeiSource
	translationKey: 'position' | 'trades'
}

const PnlTooltip: FC<{ data: PnlTooltipProps }> = memo(({ data }) => {
	const { t } = useTranslation()

	const formatOptions = {
		maxDecimals: 4,
	}

	return (
		<TooltipContentContainer>
			<ToolTipRow>
				<Body>{t(`futures.market.user.${data.translationKey}.pnl`)}:</Body>
				<Currency.Price price={data.pnl} colored formatOptions={formatOptions} />
			</ToolTipRow>
			<ToolTipRow>
				<Body>{t(`futures.market.user.${data.translationKey}.cost`)}:</Body>
				<Currency.Price price={wei(data.feesPaid).mul(-1)} colored formatOptions={formatOptions} />
			</ToolTipRow>
			<ToolTipRow>
				<Body>{t(`futures.market.user.${data.translationKey}.funding`)}:</Body>
				<Currency.Price price={data.funding} colored formatOptions={formatOptions} />
			</ToolTipRow>
			{!!data.interestCharged && (
				<ToolTipRow>
					<Body>{t(`futures.market.user.${data.translationKey}.interest`)}:</Body>
					<Currency.Price price={data.interestCharged} colored formatOptions={formatOptions} />
				</ToolTipRow>
			)}
			<Divider height={1} divider />
			<ToolTipRow>
				<Body>{t(`futures.market.user.${data.translationKey}.total-pnl`)}:</Body>
				<Currency.Price price={data.totalPnl} colored formatOptions={formatOptions} />
			</ToolTipRow>
		</TooltipContentContainer>
	)
})

type ToolTipTriggerProps = {
	children: ReactNode
	tooltipDisabled?: boolean
}

export const ToolTipTrigger: FC<ToolTipTriggerProps> = memo(({ children, tooltipDisabled }) => {
	return (
		<TriggerContainer>
			<FlexDivRow style={{ width: '75px', justifyContent: 'flex-start' }}>{children}</FlexDivRow>
			{!tooltipDisabled ? <InfoIcon /> : undefined}
		</TriggerContainer>
	)
})

const TooltipContentContainer = styled(FlexDivCol)`
	gap: 6px;
`

const ToolTipRow = styled(FlexDivRow)`
	justify-content: space-between;
`

const Divider = styled(Spacer)`
	width: 100%;
	margin: 4px 0;
`

const TriggerContainer = styled(FlexDivRow)`
	align-items: center;
	justify-content: center;
	gap: 6px;
`

export default PnlTooltip
